<script setup lang="ts">
import { useClipboard } from "@vueuse/core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

interface applicationError {
  statusCode?: number;
  message?: string;
  description?: string;
  [key: string]: any;
}

const props = defineProps<{ error: applicationError }>();

const { t } = useI18n();

const errorTitles = {
  400: 'Bad request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Page not found',
  500: 'Internal server error',
};

const showTechnicalInfo = ref(true);

const pageTitle = computed(() => {
  const statusCode = props.error.statusCode;
  if (typeof statusCode === 'number') {
    return errorTitles[statusCode as keyof typeof errorTitles] || 'Error';
  }

  return 'Error';
});

const { copy, copied, isSupported } = useClipboard();
const { $sentry } = useNuxtApp();

if (props.error) {
  try {
    $sentry.captureException(props.error);
  } catch (error) {
    console.error('Error capturing exception', error);
  }
}

const getErrorText = () => {
  let text = '';

  text += 'Status code: ' + props.error.statusCode + '; \n\n';

  text += 'Message: ' + props.error.message + '; \n\n';

  if (props.error.description) {
    {text += 'Description: ' + props.error.description + '; \n\n';}
  }

  text += 'Status message: ' + props.error.statusMessage + '; \n\n';

  text += 'Error cause: ' + JSON.stringify(props.error.cause) + ';';

  return text;
};

useHead({
  title: `${pageTitle.value}`,
});
</script>

<template>
  <div class="application-error">
    <NuxtLayout name="default">
      <div class="max-w-sm mx-auto px-4">
        <div class="pt-12 pb-24">
          <div class="flex flex-col items-center text-center">
            <img
              v-if="error.statusCode !== 500"
              src="@images/illustrations/empty-state-nora.png"
              alt="Not found"
            >
            <div
              v-if="error.statusCode === 403"
              class="text-2xl font-bold text-neutral-100"
            >
              <FontAwesomeIcon icon="lock" /> {{t('error.error_code_403')}}
            </div>
            <div
              v-else-if="error.statusCode === 500"
              class="text-2xl font-bold text-neutral-100"
            >
              {{t('error.error_code_500')}}
            </div>
            <div
              v-else
              class="text-2xl font-bold text-neutral-100"
            >
              {{t('error.error_code_404')}}
            </div>

            <div class="text-neutral-80 mt-4">
              {{t('error.error_uplift')}}
            </div>
          </div>

          <div class="flex flex-col gap-3 mt-8">
            <NuxtLink to="/dashboard">
              <Button
                outlined
                severity="secondary"
                class="text-left flex items-center gap-2 w-full py-3 border-neutral-30"
              >
                <FontAwesomeIcon icon="fa-house" />

                <span>
                  {{t('error.back_to_home')}}
                  <NuxtLink to="/dashboard">
                    {{t('error.home_page')}}
                  </NuxtLink>
                </span>
              </Button>
            </NuxtLink>

            <NuxtLink to="/courses">
              <Button
                outlined
                severity="secondary"
                class="text-left flex items-center gap-2 w-full py-3 border-neutral-30"
              >
                <FontAwesomeIcon icon="fa-book" />

                <span>
                  {{t('error.browse_courses')}}
                  <NuxtLink to="/courses">
                    {{t('error.course_library')}}
                  </NuxtLink>
                </span>
              </Button>
            </NuxtLink>
          </div>

          <Divider class="my-8" />

          <div class="text-center text-18 font-bold text-neutral-100 mb-2">
            {{t('error.technical_details')}}
          </div>

          <div
            v-if="showTechnicalInfo"
            class="flex flex-col gap-2 mb-4"
          >
            <Card
              v-if="error.statusMessage || error.cause"
              class="shadow-none mt-2 overflow-x border border-neutral-30 !rounded-lg"
              :pt="{body: '!p-4'}"
            >
              <template #content>
                <code
                  class="block whitespace-pre-line text-wrap break-all"
                  v-text="getErrorText()"
                />
              </template>
            </Card>
          </div>

          <Button
            v-if="isSupported"
            :severity="!copied ? 'primary' : 'success'"
            class="w-full"
            @click="copy(getErrorText())"
          >
            <FontAwesomeIcon
              v-if="!copied"
              icon="fa-copy"
            />
            <FontAwesomeIcon
              v-else
              icon="fa-check"
            />
            <span class="font-bold">Copy error details</span>
          </Button>
        </div>
      </div>
    </NuxtLayout>
  </div>
</template>